import React, { useMemo, useState } from "react";
import { Button } from "primereact/button";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { SelectButton } from "primereact/selectbutton";

import { EroundCatalogue, ModelMapping, UnmappedModel } from "../../../types/eroundProvisioning";
import { connectorSummary } from "../../../utils/eroundCatalogue";
import {
  ConnectorKind,
  Current,
  Suggestion,
  WallboxHints,
  hintsFromName,
  suggestEroundModels,
} from "../../../utils/eroundSuggestions";

const CONNECTORS = [
  { label: "Socket", value: "socket" },
  { label: "Cable", value: "cable" },
  { label: "Don't know", value: "unknown" },
];
const CURRENTS = [
  { label: "AC", value: "AC" },
  { label: "DC", value: "DC" },
];

// "22", "7,4" or "7.4"; anything else is no power.
const parsePower = (text: string): number | null => {
  const value = Number(text.trim().replace(",", "."));
  return text.trim() !== "" && Number.isFinite(value) && value > 0 ? value : null;
};

const describeHints = (hints: WallboxHints): string =>
  [
    hints.current,
    hints.powerKw !== null ? `${hints.powerKw} kW` : null,
    hints.connector ? `Type 2 ${hints.connector}` : null,
    `${hints.chargingPoints} charging point${hints.chargingPoints === 1 ? "" : "s"}`,
  ]
    .filter(Boolean)
    .join(" · ");

/** The stand-in reason a suggestion proposes: the decision it repeats, and what the eRound model has. */
export const suggestedReason = (suggestion: Suggestion): string => {
  const decision = suggestion.decisions[0];
  const like = decision ? `Like the stand-in for ${decision.onboardingManufacturer} ${decision.onboardingModel}. ` : "";
  return `${like}${connectorSummary(suggestion.model)}.`;
};

// Suggested eRound models for one unmapped wallbox (SOPS-187). Nothing here is saved: Use this only fills the
// mapping form, whose Save stays the one write.
const ModelSuggestions: React.FC<{
  wallbox: UnmappedModel;
  catalogue: EroundCatalogue;
  mappings: ModelMapping[];
  onUse: (suggestion: Suggestion) => void;
  // What the customer entered for an unlisted wallbox (CWA-229); otherwise the name is read.
  initialHints?: WallboxHints | null;
}> = ({ wallbox, catalogue, mappings, onUse, initialHints }) => {
  const [hints, setHints] = useState<WallboxHints>(
    () => initialHints ?? hintsFromName(wallbox.onboardingManufacturer, wallbox.onboardingModel)
  );
  const [powerText, setPowerText] = useState(() => (hints.powerKw === null ? "" : String(hints.powerKw)));
  const suggestions = useMemo(
    () =>
      suggestEroundModels(
        { manufacturer: wallbox.onboardingManufacturer, model: wallbox.onboardingModel },
        hints,
        catalogue,
        mappings
      ),
    [wallbox, hints, catalogue, mappings]
  );
  const id = (field: string) => `${wallbox.modelKey}-suggest-${field}`;

  return (
    <div className="surface-50 border-round p-3 mb-3">
      <div className="font-semibold mb-2">Suggestions</div>
      {initialHints && <div className="text-sm text-600 mb-2">Filled in from what the customer entered.</div>}
      <div className="flex flex-wrap align-items-end gap-3 mb-3">
        <div>
          <label htmlFor={id("power")} className="block text-sm mb-1">
            Wallbox power (kW)
          </label>
          <InputText
            id={id("power")}
            value={powerText}
            inputMode="decimal"
            placeholder="e.g. 22"
            className="w-6rem"
            onChange={(e) => {
              setPowerText(e.target.value);
              setHints({ ...hints, powerKw: parsePower(e.target.value) });
            }}
          />
        </div>
        <div>
          <span className="block text-sm mb-1">Wallbox connector</span>
          <SelectButton
            value={hints.connector ?? "unknown"}
            options={CONNECTORS}
            allowEmpty={false}
            className="segmented-select"
            aria-label="Wallbox connector"
            onChange={(e) =>
              setHints({ ...hints, connector: e.value === "unknown" ? null : (e.value as ConnectorKind) })
            }
          />
        </div>
        <div>
          <span className="block text-sm mb-1">Wallbox current</span>
          <SelectButton
            value={hints.current}
            options={CURRENTS}
            allowEmpty={false}
            className="segmented-select"
            aria-label="Wallbox current"
            onChange={(e) => setHints({ ...hints, current: e.value as Current })}
          />
        </div>
        <div>
          <label htmlFor={id("points")} className="block text-sm mb-1">
            Wallbox charging points
          </label>
          <InputNumber
            inputId={id("points")}
            value={hints.chargingPoints}
            min={1}
            useGrouping={false}
            onValueChange={(e) => setHints({ ...hints, chargingPoints: e.value ?? 1 })}
          />
        </div>
      </div>
      {suggestions.length === 0 ? (
        <p className="m-0 text-600">
          {hints.powerKw === null
            ? "Enter the wallbox's power and connector to get suggestions."
            : `No eRound model matches ${describeHints(hints)}.`}
        </p>
      ) : (
        <ul className="list-none p-0 m-0">
          {suggestions.map((s) => (
            <li
              key={`${s.manufacturer.name}|${s.model.name}`}
              className="flex align-items-start justify-content-between gap-3 py-2 border-top-1 surface-border"
            >
              <div>
                <div className="font-medium">
                  {s.manufacturer.name} · {s.model.name}
                </div>
                <small className="block text-600">{connectorSummary(s.model)}</small>
                <small className="block">{s.reasons.join(" · ")}</small>
                {s.warnings.map((w) => (
                  <small key={w} className="block text-orange-600">
                    {w}
                  </small>
                ))}
              </div>
              <Button
                label="Use this"
                className="p-button-outlined p-button-sm w-auto flex-shrink-0"
                aria-label={`Use ${s.manufacturer.name} ${s.model.name}`}
                onClick={() => onUse(s)}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default ModelSuggestions;
